/** @jsx jsx */
import { css, jsx } from "@emotion/core"

import { IComponentProps } from "@cd2/tools"
import { BaseButton, Icon } from "@cd2/components"
import { icons } from "./icons"
import { brandColors, colors, themeColors } from "../Components/EOSThemeProvider"
import { SageLogo } from "../stories/quoteOrders/SageLogo"

type IButton = IComponentProps<typeof BaseButton>
type IButtonConfig = { [key: string]: IButton }

const defaultButtonTheme: IButton["theme"] = {
  boxShadow: "none",
  buttonColor: brandColors.darkMint,
  hoverColor: brandColors.mint,
  fontSize: 14,
}
const largeButton: IButton["theme"] = {
  boxShadow: "none",
  padding: {
    sides: 18,
    innerSides: 10,
    topAndBottom: 10,
  },
  fontSize: 16,
}
const largeBlueButton: IButton["theme"] = {
  ...largeButton,
  buttonColor: brandColors.darkNight,
  hoverColor: brandColors.night,
}
const tableFilterButtons: IButton["theme"] = {
  padding: 11.5,
  fontSize: 13,
}

function typeButtonConfig<T extends IButtonConfig>(val: T): T {
  return val
}

export const buttonConfigs = typeButtonConfig({
  generalMint: {
    theme: defaultButtonTheme,
    children: "...",
  },
  generalNight: {
    theme: {
      ...defaultButtonTheme,
      buttonColor: brandColors.night,
      hoverColor: brandColors.lightNight,
    },
    children: "...",
  },
  generalNightOutline: {
    theme: {
      ...defaultButtonTheme,
      buttonColor: brandColors.night,
      hoverColor: "#f5f5f5",
    },
    buttonStyle: "outline",
    children: "...",
  },
  customerCta: {
    theme: {
      borderColor: brandColors.mint,
      borderRadius: 2,
      color: brandColors.night,
      buttonColor: brandColors.mint,
      fontSize: 18,
      padding: { topAndBottom: 14, sides: 40 },
    },
    children: "",
  },
  small: {
    theme: {
      fontSize: 12,
      padding: 8,
    },
    children: "...",
  },
  create: {
    theme: defaultButtonTheme,
    children: "Create new",
    renderLeft: () => <Icon icon={icons.new} color={brandColors.mintPop} />,
  },
  delete: {
    theme: { ...defaultButtonTheme, buttonColor: themeColors.red, hoverColor: themeColors.darkRed },
    children: "Delete",
    renderLeft: () => <Icon icon={icons.delete} color={brandColors.mintPop} />,
  },
  download: {
    theme: defaultButtonTheme,
    children: "",
    renderLeft: () => <Icon icon={icons.download} color={themeColors.lightBlue} />,
  },
  toggleButtonInactive: {
    theme: {
      ...tableFilterButtons,
      buttonColor: "#f0f1f3",
      hoverColor: "rgba(32,33,36,0.1)",
      borderColor: "#ddd",
      boxShadow: "none",
    },
    children: "Inactive",
    renderLeft: () => <Icon icon={icons.toggleOff} size={20} color={themeColors.grey} />,
  },
  toggleButtonActive: {
    children: "Active",
    theme: {
      ...tableFilterButtons,
      boxShadow: "none",
      buttonColor: "#DF9C38",
      borderColor: "#DF9C38",
    },
    renderLeft: () => <Icon icon={icons.toggleOn} size={20} />,
  },
  formSaveButton: {
    theme: defaultButtonTheme,
    renderLeft: () => <Icon icon={icons.save} color={brandColors.night} />,
    type: "submit",
    children: "Save",
  },
  largeBlueButton: {
    theme: largeBlueButton,
    renderLeft: () => <Icon icon={icons.save} color={brandColors.white} />,
    type: "submit",
    children: "Save",
  },
  largeOutlineButton: {
    theme: largeButton,
    renderLeft: () => <Icon icon={icons.save} color={brandColors.night} />,
    type: "submit",
    children: "Save",
  },
  formSaveButtonSaving: {
    theme: defaultButtonTheme,
    renderLeft: () => <Icon icon={icons.spinner} color={themeColors.lightBlue} spin />,
    type: "submit",
    children: "Save",
    disabled: true,
  },
  saveButton: {
    theme: defaultButtonTheme,
    renderLeft: () => <Icon icon={icons.save} color={themeColors.lightBlue} />,
    children: "Save",
  },
  cancelButton: {
    theme: {
      buttonColor: "#f0f1f3",
      hoverColor: "rgba(32,33,36,0.1)",
      borderColor: "#aaa",
      boxShadow: "none",
    },
    children: "Cancel",
  },
  linkToEdit: {
    theme: defaultButtonTheme,
    children: "Edit",
    renderLeft: () => <Icon icon={icons.edit} color={brandColors.mintPop} />,
  },
  disabledDueToArchive: {
    theme: {
      buttonColor: "#f0f1f3",
      hoverColor: "rgba(32,33,36,0.1)",
      borderColor: "#aaa",
      boxShadow: "none",
    },
    children: "Archived",
    renderLeft: () => <Icon icon={icons.lock} color={themeColors.grey} />,
    disabled: true,
  },
  beingEdited: {
    theme: {
      buttonColor: "black",
      hoverColor: "black",
      boxShadow: "none",
    },
    children: "Being edited",
    renderLeft: () => <Icon icon={icons.lock} color={themeColors.grey} />,
    disabled: true,
  },
  pendingActionLight: {
    theme: {
      ...tableFilterButtons,
      buttonColor: "#ffffff",
      borderColor: "#bbb",
      boxShadow: "none",
    },
    children: "Inactive",
    renderLeft: () => (
      <div
        css={css`
          width: 12px;
          height: 12px;
          background-color: ${themeColors.alertRed};
          border-radius: 6px;
        `}
      />
    ),
  },
  pendingActionDark: {
    theme: {
      ...tableFilterButtons,
      buttonColor: themeColors.darkestBlue,
      boxShadow: "none",
    },
    children: "Inactive",
    renderLeft: () => (
      <div
        css={css`
          width: 12px;
          height: 12px;
          background-color: ${themeColors.alertRed};
          border-radius: 6px;
        `}
      />
    ),
  },
  linkToSection: {
    theme: {
      padding: { topAndBottom: 12, sides: 0, innerSides: 12 },
      boxShadow: "none",
      buttonColor: "white",
      fontSize: 13,
      color: "#555",
      hoverColor: "#f5f5f5",
    },
    children: "",
    renderLeft: () => <Icon icon={icons.productGroups} color="#aaa" />,
  },
  quantityButtonAdd: {
    theme: {
      padding: 12,
      boxShadow: "none",
      buttonColor: "#f5f5f5",
      fontSize: 16,
    },
    block: true,
    children: <Icon icon={icons.plus} color="#1b1b1b" />,
  },
  quantityButtonMinus: {
    theme: {
      padding: 12,
      boxShadow: "none",
      buttonColor: "#f5f5f5",
      fontSize: 16,
    },
    block: true,
    children: <Icon icon={icons.collapse} color="#1b1b1b" />,
  },
  quantityButtonAddGreen: {
    theme: {
      padding: 12,
      boxShadow: "none",
      buttonColor: "#f5f5f5",
      fontSize: 16,
    },
    block: true,
    children: <Icon icon={icons.plus} color={brandColors.darkMint} />,
  },
  quantityButtonMinusGreen: {
    theme: {
      padding: 12,
      boxShadow: "none",
      buttonColor: "#f5f5f5",
      fontSize: 16,
    },
    block: true,
    children: <Icon icon={icons.collapse} color={brandColors.darkMint} />,
  },
  cart: {
    theme: {
      padding: { topAndBottom: 16, sides: 24 },
      boxShadow: "none",
      buttonColor: brandColors.darkMint,
      fontSize: 16,
    },
    block: true,
    children: "",
  },
  downloadPdf: {
    theme: {
      boxShadow: "none",
      buttonColor: "#AA0C01",
      borderColor: "#AA0C01",
    },
    renderLeft: () => <Icon icon={icons.pdf} color="white" />,
    renderRight: () => <Icon icon={icons.download} color="rgba(255,255,255,0.8)" />,
    children: "Download",
  },
  downloadXls: {
    theme: {
      boxShadow: "none",
      buttonColor: "#1D6C42",
      borderColor: "#1D6C42",
    },
    renderLeft: () => <Icon icon={icons.xls} color="white" />,
    renderRight: () => <Icon icon={icons.download} color="rgba(255,255,255,0.8)" />,
    children: "Download",
  },
  downloadCsv: {
    theme: {
      boxShadow: "none",
    },
    renderLeft: () => <Icon icon={icons.file} color="white" />,
    renderRight: () => <Icon icon={icons.download} color="rgba(255,255,255,0.8)" />,
    children: "Download",
  },
  dpdButton: {
    theme: { buttonColor: "#dc0032", borderColor: "#bb0033" },
    renderLeft: () => <Icon icon={icons.xls} color="white" />,
    children: "",
  },
  smallInlineSaveButton: {
    theme: {
      padding: 8,
      boxShadow:
        "rgb(0 0 0 / 0%) 0px 0px 0px 0px, rgb(0 0 0 / 0%) 0px 0px 0px 0px, rgb(0 0 0 / 12%) 0px 1px 1px 0px, rgb(60 66 87 / 16%) 0px 0px 0px 1px, rgb(0 0 0 / 0%) 0px 0px 0px 0px, rgb(0 0 0 / 0%) 0px 0px 0px 0px, rgb(60 66 87 / 8%) 0px 2px 5px 0px",
      buttonColor: "white",
      fontSize: 13,
      color: "#555",
      hoverColor: "#f5f5f5",
    },
    children: "Save",
  },
  sageButton: {
    theme: {
      buttonColor: colors.sage,
    },
    renderLeft: () => <Icon icon={icons.upload} color="white" />,
    renderRight: () => (
      <div
        css={css`
          width: 40px;
          position: relative;
          height: 10px;
        `}
      >
        <SageLogo color="#FFF" />
      </div>
    ),
    children: "Export to Sage",
  },
  blackBorderedBack: {
    renderLeft: () => <Icon icon={icons.chevronLeft} />,
    theme: {
      buttonColor: "transparent",
      borderColor: brandColors.darkNight,
      color: brandColors.darkNight,
      borderRadius: 2,
      hoverColor: "white",
      padding: {
        topAndBottom: 8,
        sides: 16,
        innerSides: 16,
      },
    },
    children: "Back",
  },
})
